import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { gerarParcelas } from '@core/util/parcelaUtil';
import { cancelarAlert } from 'app/services/alerts';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import NP from 'number-precision';
import React, { useContext, useEffect } from 'react';
import { FaShoppingCart } from 'react-icons/fa';
import * as Yup from 'yup';
import { CompraContext } from '../../../CompraContext';
import {
  useAproveitamentoCreditoInitialValue,
  useCategoriasInitialValue,
  useCodigoInitialValue,
  useFornecedorInitialValue,
  usePagamentoInitialValue,
  useProdutosInitialValue,
} from '../compraInitialValues';
import { compraModais } from '../compraModais';
import {
  useAproveitamentoCreditoSchema,
  useCategoriasSchema,
  useDiferencaCategoriasSchema,
  useFornecedorSchema,
  usePagamentoSchema,
  useProdutosSchema,
} from '../compraSchema';
import CompraProdutoForm from './CompraProdutoForm';

const HOJE = moment();

const CompraProdutoModal = ({ onSubmit, empresaIbsCbs = false }) => {
  const [compra] = useContext(CompraContext).compraAtual;
  const [modal, setModal] = useContext(CompraContext).modal;
  const [search] = useContext(CompraContext).search;
  const [page] = useContext(CompraContext).page;
  const [size] = useContext(CompraContext).size;
  const [sort] = useContext(CompraContext).sort;

  const initialValues = {
    id: compra?.id || '',
    codigo: useCodigoInitialValue(compra?.compraId),
    dataCompra: compra?.dataCompra ? moment(compra.dataCompra) : HOJE,
    fornecedor: useFornecedorInitialValue(compra),
    categorias: useCategoriasInitialValue(compra),
    produtos: useProdutosInitialValue(compra),
    valorTotal:
      compra?.comprasProdutos?.reduce(
        (acc, produto) => NP.plus(acc, NP.times(produto?.quantidade || 0, produto?.valor || 0)),
        0
      ) || 0,
    pagamento: usePagamentoInitialValue(compra),
    documentoFiscal: compra?.documentoFiscal,
    dataEmissao: compra?.dataEmissao,
    anexos: compra?.anexos || [],
    observacao: compra?.observacao || '',
    aproveitamentoCredito: useAproveitamentoCreditoInitialValue(compra),
  };

  const compraProdutoSchema = Yup.object().shape({
    dataCompra: Yup.object()
      .nullable()
      .required(mensagemCampoObrigatorio('a data da compra')),
    fornecedor: useFornecedorSchema(),
    categorias: useCategoriasSchema(),
    diferencaCategorias: useDiferencaCategoriasSchema(),
    produtos: useProdutosSchema(),
    pagamento: usePagamentoSchema(),
    aproveitamentoCredito: useAproveitamentoCreditoSchema(),
  });

  const close = () => {
    formik.resetForm(initialValues);
    setModal('');
  };

  const handleSubmit = async (data = {}) => {
    if (!formik?.dirty) {
      close();
      return;
    }

    const compra = JSON.parse(JSON.stringify(data));

    compra.fornecedorId = compra?.fornecedor?.id;
    compra.categorias.forEach((categoria) => {
      categoria.classeId = categoria.classe.id;
    });
    compra.comprasProdutos = compra.produtos?.map(
      ({ id, produto: { id: produtoId }, quantidade, valor, imobilizado }) => ({
        id,
        produtoId,
        quantidade,
        valor,
        imobilizado,
      })
    );

    if (compra.pagamento?.avista) {
      compra.pagamento.avista.contaId = compra.pagamento.avista.conta?.id;
      delete compra.pagamento.avista.conta;
    }

    if (compra.pagamento?.parcelado) {
      if (!compra.pagamento.parcelado.parcelas) {
        const quantidade = compra?.pagamento?.parcelado?.quantidade || 0;
        const dataPrimeiraParcela = compra?.dataCompra ? moment(compra.dataCompra) : moment();

        const { valorLiquido } = compra;

        compra.pagamento.parcelado = gerarParcelas(quantidade, dataPrimeiraParcela, valorLiquido);
      } else {
        compra.pagamento.parcelado = compra.pagamento.parcelado.parcelas;
      }
    }

    if (compra?.pagamento?.condicaoPagamento === 'A_VISTA') {
      delete compra.pagamento.parcelado;

      if (!compra?.pagamento?.avista?.compraPaga) {
        delete compra.pagamento.avista.dataPagamento;
        delete compra.pagamento.avista.contaId;
        delete compra.pagamento.avista.formaPagamento;
        delete compra.pagamento.avista.numeroCheque;
        delete compra.pagamento.avista.dataLiquidacao;
      }
    } else {
      delete compra.pagamento.avista;
    }

    // Apaga dados que a api não precisa
    delete compra.fornecedor;
    delete compra.categoria;
    delete compra.produtos;

    const res = await onSubmit(compra, { q: search, page, size, sort }, 'PRODUTO');

    if (res) {
      close();
    }
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const formik = useFormik({
    validationSchema: compraProdutoSchema,
    initialValues,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compra]);

  return (
    <SimpleDialog fullScreen open={modal === compraModais.comprarProduto} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Compra de produto',
            icon: <FaShoppingCart />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <CompraProdutoForm empresaIbsCbs={empresaIbsCbs} />
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CompraProdutoModal;
